import { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMoneyBillWave,
  faClock,
  faUpload,
  faCheckCircle,
  faDollarSign,
  faQrcode,
} from "@fortawesome/free-solid-svg-icons";
import {
  Button,
  LoadingSkeleton,
  PriceBreakdownCell,
  StatCard,
} from "../../components/ui";
import {
  getDoctorPayments,
  getPaymentsDoctors,
  getPaymentsOverview,
  transferMonthPayments,
  uploadReceipt,
} from "./paymentsApi";
import { formatDate, formatMonthLabel, formatMoney } from "./paymentsUtils";
import DoctorSelector from "./DoctorSelector";
import PaymentMessage from "./PaymentMessage";
import TransferModal from "./TransferModal";
import "./DoctorPaymentsTab.css";

export default function DoctorPaymentsTab() {
  const [overview, setOverview] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState("");
  const [doctorPayments, setDoctorPayments] = useState(null);

  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingDoctors, setLoadingDoctors] = useState(true);
  const [loadingPayments, setLoadingPayments] = useState(false);

  const [transferModal, setTransferModal] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    (async () => {
      setLoadingOverview(true);
      try {
        setOverview(await getPaymentsOverview());
      } catch (err) {
        console.error("Error fetching payments overview:", err);
      } finally {
        setLoadingOverview(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoadingDoctors(true);
      try {
        setDoctors((await getPaymentsDoctors()) || []);
      } catch (err) {
        console.error("Error fetching doctors:", err);
      } finally {
        setLoadingDoctors(false);
      }
    })();
  }, []);

  const fetchDoctorPayments = useCallback(async (doctorId) => {
    if (!doctorId) {
      setDoctorPayments(null);
      return;
    }
    setLoadingPayments(true);
    try {
      setDoctorPayments(await getDoctorPayments(doctorId));
    } catch (err) {
      console.error("Error fetching doctor payments:", err);
      setDoctorPayments(null);
    } finally {
      setLoadingPayments(false);
    }
  }, []);

  useEffect(() => {
    fetchDoctorPayments(selectedDoctorId);
  }, [selectedDoctorId, fetchDoctorPayments]);

  const openTransferModal = (month) => {
    setTransferModal(month);
    setMessage({ type: "", text: "" });
  };

  const closeTransferModal = () => {
    setTransferModal(null);
    setMessage({ type: "", text: "" });
  };

  const handleTransferSubmit = async (receiptFile) => {
    if (!transferModal) return;
    if (!receiptFile) {
      setMessage({
        type: "error",
        text: "You must upload a receipt to confirm the transfer.",
      });
      return;
    }
    setSubmitting(true);
    setMessage({ type: "", text: "" });
    try {
      const receiptUrl = await uploadReceipt(receiptFile);
      const result = await transferMonthPayments({
        doctorID: selectedDoctorId,
        year: transferModal.year,
        month: transferModal.month,
        receipt: receiptUrl,
      });
      closeTransferModal();
      setMessage({
        type: "success",
        text: `Transferred ${result?.transferred || 0} sessions.`,
      });
      fetchDoctorPayments(selectedDoctorId);
    } catch (err) {
      setMessage({
        type: "error",
        text:
          err.response?.data?.message ||
          err.response?.data ||
          "Transfer failed.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const selectedDoctor = doctors.find(
    (doctor) => doctor.id === selectedDoctorId,
  );

  return (
    <>
      <PaymentMessage message={message} />

      <div className="payments-gains-row">
        <StatCard
          title="Platform Revenue"
          value={formatMoney(overview?.totalPlatformRevenue)}
          icon={<FontAwesomeIcon icon={faMoneyBillWave} />}
          color="var(--primary-500)"
          loading={loadingOverview}
        />
        <StatCard
          title="Total Paid to Doctors"
          value={formatMoney(overview?.totalPaidToDoctors)}
          icon={<FontAwesomeIcon icon={faCheckCircle} />}
          color="var(--success-500)"
          loading={loadingOverview}
        />
        <StatCard
          title="Total Unpaid"
          value={formatMoney(overview?.totalUnpaidToDoctors)}
          icon={<FontAwesomeIcon icon={faClock} />}
          color="var(--warning-500)"
          loading={loadingOverview}
        />
        <StatCard
          title="Total Discounted (Vouchers)"
          value={formatMoney(overview?.totalVoucherDiscount)}
          icon={<FontAwesomeIcon icon={faDollarSign} />}
          color="var(--danger-500)"
          loading={loadingOverview}
        />
      </div>

      <DoctorSelector
        doctors={doctors}
        selectedDoctor={selectedDoctor}
        onSelect={setSelectedDoctorId}
        loading={loadingDoctors}
      />

      {selectedDoctorId && (
        <div className="payments-doctor-section">
          {loadingPayments ? (
            <div className="payments-loading">
              <LoadingSkeleton width="100%" height="40px" />
              {[1, 2, 3].map((index) => (
                <LoadingSkeleton key={index} width="100%" height="80px" />
              ))}
            </div>
          ) : doctorPayments ? (
            <>
              <div className="payments-doctor-summary">
                <div className="payments-summary-row">
                  <StatCard
                    title="Platform Revenue"
                    value={formatMoney(doctorPayments.platformRevenue)}
                    icon={<FontAwesomeIcon icon={faMoneyBillWave} />}
                    color="var(--primary-500)"
                  />
                  <StatCard
                    title="Paid to Doctor"
                    value={formatMoney(doctorPayments.paidAmount)}
                    icon={<FontAwesomeIcon icon={faCheckCircle} />}
                    color="var(--success-500)"
                  />
                  <StatCard
                    title="Pending Transfer"
                    value={formatMoney(doctorPayments.unpaidAmount)}
                    icon={<FontAwesomeIcon icon={faClock} />}
                    color="var(--warning-500)"
                  />
                  <StatCard
                    title="Total Discounted (Vouchers)"
                    value={formatMoney(doctorPayments.voucherDiscountTotal)}
                    icon={<FontAwesomeIcon icon={faDollarSign} />}
                    color="var(--danger-500)"
                  />
                </div>
                {selectedDoctor?.instapayQR && (
                  <div className="payments-qr-card">
                    <div className="payments-qr-icon">
                      <FontAwesomeIcon icon={faQrcode} />
                    </div>
                    <div className="payments-qr-info">
                      <span className="payments-qr-label">InstaPay QR</span>
                      <img
                        src={
                          selectedDoctor.instapayQR.startsWith("http")
                            ? selectedDoctor.instapayQR
                            : `${process.env.REACT_APP_BACKEND_URL}${selectedDoctor.instapayQR}`
                        }
                        alt="InstaPay QR"
                        className="payments-qr-image"
                      />
                    </div>
                  </div>
                )}
              </div>

              {doctorPayments.months?.length > 0 ? (
                <div className="payments-months">
                  {doctorPayments.months.map((month) => {
                    const unpaidThisMonth = month.totalDue - month.totalPaid;
                    return (
                      <div
                        key={`${month.year}-${month.month}`}
                        className="month-group"
                      >
                        <div className="month-header">
                          <h3 className="month-title">
                            {formatMonthLabel(month)}
                          </h3>
                          <span className="month-summary">
                            Due: {formatMoney(month.totalDue)} &middot; Paid:{" "}
                            {formatMoney(month.totalPaid)}
                          </span>
                          {unpaidThisMonth > 0 && (
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => openTransferModal(month)}
                            >
                              <FontAwesomeIcon
                                icon={faUpload}
                                style={{ marginRight: 6 }}
                              />
                              Confirm Transfer
                            </Button>
                          )}
                        </div>
                        <table className="month-table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Patient</th>
                              <th>Price</th>
                              <th>Doctor Cut</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {month.sessions.map((session) => (
                              <tr key={session.id}>
                                <td>{formatDate(session.date)}</td>
                                <td>{session.patientName || "—"}</td>
                                <PriceBreakdownCell
                                  price={session.price}
                                  systemCut={session.systemCut}
                                  voucherCode={session.voucherCode}
                                  discountType={session.discountType}
                                  discountValue={session.discountValue}
                                  discountAmountApplied={
                                    session.discountAmountApplied
                                  }
                                  doctorCut={session.doctorCut}
                                  finalPrice={session.finalPrice}
                                />
                                <td>{formatMoney(session.doctorCut)}</td>
                                <td>
                                  <span
                                    className={`payments-status-badge ${
                                      session.transferred
                                        ? "badge-paid"
                                        : "badge-pending"
                                    }`}
                                  >
                                    {session.transferred
                                      ? "Transferred"
                                      : "Pending"}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="payments-empty">
                  <p>No completed sessions found for this doctor.</p>
                </div>
              )}
            </>
          ) : (
            <div className="payments-empty">
              <p>Could not load payment data for this doctor.</p>
            </div>
          )}
        </div>
      )}

      <TransferModal
        month={transferModal}
        submitting={submitting}
        message={message}
        onClose={closeTransferModal}
        onConfirm={handleTransferSubmit}
      />
    </>
  );
}
